/* eslint-disable brace-style */
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { kvkNumberValidator } from '@next/arable-lib/shared';
import {
  AbstractDialogResultData,
  AbstractFormDialog,
  AbstractFormDialogData,
} from '@next/core-lib/dialog';
import { SiteState } from '@next/core-lib/site';
import { SelectedCustomer, UserSelectors } from '@next/core-lib/user';
import { Store, select } from '@ngrx/store';
import { Observable, ReplaySubject, takeUntil } from 'rxjs';
import { SoilType } from 'src/app/soil-type/models/soil-type.models';
import * as AppSelectors from 'src/app/store/selectors';

import { getCurrentKvkNumber } from '../../current-kvk-number';

export interface SewageSludgeCompostTransportImportRequestParams extends AbstractFormDialogData {
  kvkNumber: string;
  startDate: string;
  endDate: string;
}

@Component({
  selector: 'app-sewage-sludge-compost-transport-import-dialog',
  templateUrl: './sewage-sludge-compost-transport-import-dialog.component.html',
  styleUrls: ['./sewage-sludge-compost-import-dialog.component.scss'],
  standalone: false,
})
export class SewageSludgeCompostImportDialogComponent
  extends AbstractFormDialog<SewageSludgeCompostTransportImportRequestParams>
  implements OnInit, OnDestroy
{
  data = inject<AbstractFormDialogData>(MAT_DIALOG_DATA);
  protected readonly mdDialogRef =
    inject<
      MatDialogRef<
        SewageSludgeCompostImportDialogComponent,
        AbstractDialogResultData<SewageSludgeCompostTransportImportRequestParams>
      >
    >(MatDialogRef);
  private readonly fb = inject(FormBuilder);
  private readonly store = inject<Store<SiteState>>(Store);

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  startMinDate: Date;
  startMaxDate: Date;
  endMinDate: Date;
  endMaxDate: Date;

  selectedCustomer$: Observable<SelectedCustomer> = this.store.pipe(
    select(UserSelectors.selectCustomer),
    takeUntil(this.destroyed$),
  );

  private readonly cropYear$ = this.store.select(AppSelectors.getCropYear);

  formGroup = this.fb.group({
    startDate: [new Date(), [Validators.required]],
    endDate: [new Date(), [Validators.required]],
    kvkNumber: ['', [Validators.required, kvkNumberValidator]],
  });

  private noneSoilType: SoilType = { id: 0, name: 'Geen' };
  soilTypes: SoilType[] = [this.noneSoilType];

  ngOnInit(): void {
    this.cropYear$.subscribe((cropYear) => {
      const minStartDate = new Date(cropYear, 0, 1);
      this.startMinDate = new Date(cropYear - 1, 8, 1);
      this.startMaxDate = new Date(cropYear, 11, 31);
      this.endMinDate = new Date(cropYear, 0, 1);
      this.endMaxDate = new Date(cropYear, 11, 31);

      this.formGroup.controls.startDate.setValue(minStartDate);
      this.formGroup.controls.endDate.setValue(this.endMaxDate);
    });
    this.selectedCustomer$.subscribe((customer) => {
      this.prefillKvkNumberForRvo(customer);
    });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  confirmSewageSludgeCompostTransportFetch() {
    this.confirm({
      startDate: this.formGroup.controls.startDate.value.toISOString(),
      endDate: this.formGroup.controls.endDate.value.toISOString(),
      kvkNumber: this.formGroup.controls.kvkNumber.value,
    });
  }

  private prefillKvkNumberForRvo(customer: SelectedCustomer) {
    const kvkNumber = getCurrentKvkNumber(
      customer.shipTos.find((x) => x.externalId === customer.externalId)?.identifications ?? [],
    );

    const kvkNumberFormControl = this.formGroup.controls.kvkNumber;
    kvkNumberFormControl.setValue(kvkNumber);
    kvkNumberFormControl.addValidators(Validators.minLength(8));
    kvkNumberFormControl.updateValueAndValidity();
  }
}
